import { Column, Header, Row } from '@tanstack/react-table';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { notNil } from '../../../core/helpers/is-nil';
import { Typography } from '../../typography/typography';
import { TableRow } from '../table-row/table-row';
import { LoadingSpinner } from '../../loading-spinner/loading-spinner';
import styles from './table.module.scss';

// aria-rowindex starts at 1, so we need to adjust the row indices accordingly
const ARIA_ROW_INDEX_HEADER = 1; // Header row index
const ARIA_ROW_INDEX_ROWS_OFFSET = 2; // Since header is mandatory we offset by 2

const TableOuterContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <div className={classNames(styles.outerContainer)}>{children}</div>;
};

// Use a custom `flexRender` instead of @tanstack/react-table
// to prevent unnecessary ummounting and re-mounting of the rendering tree
// https://github.com/TanStack/table/issues/4794#issuecomment-2809392712
function flexRender<TProps extends object>(
  Comp: ((props: TProps) => React.ReactNode) | string | undefined,
  props: TProps,
): React.ReactNode {
  if (typeof Comp === 'string') {
    return Comp;
  }
  return Comp?.(props);
}

type Props<T> = {
  visibleColumns: Column<T, unknown>[];
  headers: Header<T, unknown>[];
  rows: Row<T>[];
  footers: Header<T, unknown>[];
  isLoading?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  label: string;
  paginationComponent?: React.ReactNode;
};

export const Table = <T,>({
  visibleColumns,
  headers,
  rows,
  footers,
  isLoading = false,
  hasError,
  errorMessage,
  label,
  paginationComponent,
}: Props<T>) => {
  const { t } = useTranslation();
  const hasNoData = rows.length === 0;

  const templateColumnsStylesArray = useMemo(() => {
    const getCssWidth = (column: Column<T, unknown>) => {
      const { cssWidth } = column.columnDef.meta || {};

      return cssWidth || '1fr';
    };

    const sizes = visibleColumns.map(getCssWidth);

    return sizes;
  }, [visibleColumns]);

  const templateRowsStylesArray = Array.from({ length: rows.length + 1 }, () => 'auto'); // add one row for the header

  const tableStyles: React.CSSProperties = {
    gridTemplateColumns: templateColumnsStylesArray.join(' '),
    gridTemplateRows: templateRowsStylesArray.join(' '), // We have to set the rows explicitly, to be able to span the last column over all rows
  };

  const noDataLabel = t('global.table.noData');

  const showPagination = notNil(paginationComponent);

  if (isLoading) {
    return (
      <TableOuterContainer>
        <div className={styles.loadingOverlay}>
          <LoadingSpinner />
        </div>
      </TableOuterContainer>
    );
  }

  if (hasError && !isLoading) {
    return (
      <TableOuterContainer>
        <div className={styles.fixedRow}>
          <Typography color="red" variant="smalltext">
            {errorMessage ?? t('global.table.errorCannotDisplayData')}
          </Typography>
        </div>
      </TableOuterContainer>
    );
  }

  if (hasNoData && !hasError && !isLoading) {
    return (
      <TableOuterContainer>
        <div className={styles.fixedRow}>
          <Typography color="anthrazit-50" variant="smalltext">
            {noDataLabel}
          </Typography>
        </div>
      </TableOuterContainer>
    );
  }

  const hasFooters = footers.some((footer) => notNil(footer.column.columnDef.footer));

  const ariaRowIndexFooter = rows.length + ARIA_ROW_INDEX_ROWS_OFFSET;
  const rowCountHeaderAndRows = rows.length + 1;
  const ariaRowCount = hasFooters ? rowCountHeaderAndRows + 1 : rowCountHeaderAndRows;

  return (
    <TableOuterContainer>
      <div
        aria-colcount={visibleColumns.length}
        aria-label={label}
        aria-rowcount={ariaRowCount}
        className={classNames(styles.root)}
        role="table"
        style={tableStyles}
      >
        <div className={styles.thead} role="rowgroup">
          <TableRow key="header" className={styles.trow} rowIndex={ARIA_ROW_INDEX_HEADER}>
            {headers.map((header) =>
              flexRender(header.column.columnDef.header, { ...header.getContext(), key: header.id }),
            )}
          </TableRow>
        </div>

        <div className={styles.tbody} role="rowgroup">
          {rows.map((row) => {
            return (
              <TableRow key={row.id} className={styles.trow} rowIndex={row.index + ARIA_ROW_INDEX_ROWS_OFFSET}>
                {row
                  .getVisibleCells()
                  .map((cell) => flexRender(cell.column.columnDef.cell, { ...cell.getContext(), key: cell.id }))}
              </TableRow>
            );
          })}
          {hasFooters ? (
            <TableRow key="footer" className={styles.trow} rowIndex={ariaRowIndexFooter}>
              {footers.map((footer) =>
                flexRender(footer.column.columnDef.footer, { ...footer.getContext(), key: footer.id }),
              )}
            </TableRow>
          ) : null}
        </div>
      </div>
      {showPagination && <div className={styles.paginationContainer}>{paginationComponent}</div>}
    </TableOuterContainer>
  );
};
